import React, { useEffect, useState } from 'react';
import '../../styles/Trip.css';
import axios from 'axios';
import NewTripEditor from './NewTripEditor';
import { useLocation, useNavigate } from 'react-router-dom';
import MiniGlobe from '../globe/MiniGlobe';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import plLocale from 'i18n-iso-countries/langs/pl.json';
import { useToast } from '../ToastContext';

countries.registerLocale(enLocale);
countries.registerLocale(plLocale);

const countryNameOverrides = {
  "Dem. Rep. Congo": "Democratic Republic of the Congo",
  "S. Sudan": "South Sudan",
  "W. Sahara": "Western Sahara",
  "Côte d'Ivoire": "Côte d'Ivoire",
  "Eq. Guinea": "Equatorial Guinea",
  "Central African Rep.": "Central African Republic",
  "Congo": "Republic of the Congo"
};

const Trip = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteConfirmationWidow, setShowDeleteConfirmationWidow] = useState(false);
  const [tripToDeleteId, setTripToDeleteId] = useState(null);

  const userId = localStorage.getItem('userId');

  const rawId = location.state?.id; // np. "12" z Globe.js

  // Ujednolicamy ID do czystego formatu (np. "12" lub "012") z localStorage jako zapas
  const [countryCode, setCountryCode] = useState(
    rawId ? String(rawId) : localStorage.getItem("selectedCountryCode")
  );

  console.log("ISO: ", countryCode);

  const [worldData, setWorldData] = useState(null);
  const [tripFromChosenCountry, setTripFromChosenCountry] = useState([]);
  const [moreInfo, setMoreInfo] = useState(null);
  const [goToNewTripEditor, setGoToNewTripEditor] = useState(0);
  const [pictures, setPictures] = useState([]);

  // Pobieranie danych geograficznych do mini globusa
  useEffect(() => {
    fetch('/world-data.json')
      .then((res) => res.json())
      .then((data) => setWorldData(data))
      .catch((err) => console.error("Błąd pobierania mapy:", err));
  }, []);

  // Synchronizacja wybranego ID
  useEffect(() => {
    if (countryCode) {
      localStorage.setItem("selectedCountryCode", countryCode);
    }
  }, [countryCode]);

  useEffect(() => {
    if (!countryCode || !userId || !worldData) return;

    const cleanId = String(countryCode).padStart(3, '0');

    const country = worldData.objects?.countries?.geometries?.find(
      (country) =>
        String(country.id).padStart(3, '0') === cleanId
    );

    if (!country) {
      console.error("Nie znaleziono kraju dla ID:", countryCode);
      return;
    }

    const englishName = country.properties?.name;

    const normalizedName =
      countryNameOverrides[englishName] || englishName;

    const alpha2 = countries.getAlpha2Code(
      normalizedName,
      "en"
    );

    console.log(
      "Wybrany kraj:",
      countryCode,
      "→",
      englishName,
      "→",
      alpha2
    );

    if (!alpha2) {
      console.error(
        "Nie udało się znaleźć ISO2 dla:",
        englishName
      );
      return;
    }

    axios
      .get(`${API_URL}/trips/${userId}/${alpha2}`)
      .then(response => {
        setTripFromChosenCountry(response.data);
        setMoreInfo(response.data[0]);
      })
      .catch(error =>
        console.error(
          "Błąd pobierania wycieczek z tego kraju: ",
          error
        )
      );

    axios
      .get(`${API_URL}/countries/chosenCountryInfo/${alpha2}`)
      .then(response => {
        if (response.data && response.data[0]) {
          localStorage.setItem(
            "selectedCountryName",
            response.data[0].polishName
          );

          localStorage.setItem(
            "selectedCountryDbId",
            response.data[0].id
          );
        }
      })
      .catch(error =>
        console.error(
          "Błąd informacji o kraju: ",
          error
        )
      );

  }, [countryCode, userId, worldData]);

  // Pobieranie zdjęć dla wybranego wpisu
  useEffect(() => {
    if (!moreInfo) return;

    axios.get(`${API_URL}/pictures/${userId}/${moreInfo.id}`)
      .then((response) => setPictures(response.data))
      .catch(error => console.error("Błąd pobierania zdjęć: ", error));
  }, [moreInfo, userId]);

  // Dodawanie zdjęć
  const addPhoto = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const filesAmountLimit = 30;

    if (selectedFiles.length === 0) return;

    if (selectedFiles.length > filesAmountLimit) {
      showToast(`Maksymalnie na raz możesz dodać ${filesAmountLimit} zdjęć!`, "error");
      e.target.value = null;
      return;
    }

    if (!moreInfo) {
      showToast("Wybierz wpis, do którego chcesz dodać zdjęcia!", "error")
      e.target.value = null;
      return;
    }

    if (selectedFiles.length > 5) {
      setIsUploading(true);
    }

    const formData = new FormData();
    formData.append("tripId", moreInfo.id);
    formData.append("userId", userId);
    selectedFiles.forEach(picture => formData.append("images", picture));

    try {
      await axios.post(`${API_URL}/pictures/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const response = await axios.get(`${API_URL}/pictures/${userId}/${moreInfo.id}`);
      setPictures(response.data);
      showToast("Zdjęcie zostało dodane!", "success")
    } catch (error) {
      console.error("Błąd dodawania zdjęć: ", error);
      showToast("Wystąpił błąd podczas wysyłania zdjęć.", "error")
    } finally {
      setIsUploading(false);
      e.target.value = null;
    }
  };

  // Usuwanie wpisu i zdjęcia
  const handleDeleteClick = (tripId) => {
    setTripToDeleteId(tripId);
    setShowDeleteConfirmationWidow(true);
  };

  const confirmDeleteTrip = async () => {
    if (!tripToDeleteId) return;

    try {
      if (pictures && pictures.length > 0) {
        const deletePhotoPromises = pictures.map((photo) =>
          axios.delete(`${API_URL}/pictures/${photo.id}`)
        );
        await Promise.all(deletePhotoPromises);
      }

      await axios.delete(`${API_URL}/trips/trip/${tripToDeleteId}`);

      showToast("Usunięto wpis", "info");
      setShowDeleteConfirmationWidow(false);
      navigate('/globus');
    } catch (error) {
      console.error("Błąd usuwania wpisu: ", error);
      showToast("Nie udało się usunąć wpisu", "error");
      setShowDeleteConfirmationWidow(false);
    }
  };

  const deletePhoto = (photoId) => {
    axios.delete(`${API_URL}/pictures/${photoId}`)
      .then(() => setPictures(prev => prev.filter(p => p.id !== photoId)))
      .catch(error => console.error("Błąd usuwania zdjęcia: ", error));
  };

  if (goToNewTripEditor) return <NewTripEditor />;

  return (

    <div className='grid'>
      {/* Lewa strona - Mini Globus i Lista wpisów */}
      <div className='leftSide'>
        <h1 className="countryTitle">
          {localStorage.getItem("selectedCountryName") || "Błąd pobierania nazwy kraju"}
        </h1>
        <div className='topLeft'>
          <div id="miniCountry">
            <MiniGlobe
              worldData={worldData}
              selectedCountryId={String(rawId).padStart(3, '0')}
              width={280}
              height={280}
            />
          </div>
        </div>

        <div className='bottomLeft'>
          <h1>Wszystkie wpisy</h1>
          <div className='oldTrips'>
            {tripFromChosenCountry.length === 0 ? <center style={{ color: "#4a629b" }}>Brak wpisów dla tego kraju</center> : <>
              {tripFromChosenCountry.map((element, index) => (
                <p key={index}>
                  {element.title}
                  <button onClick={() => setMoreInfo(element)} className='moreInfo'>Więcej</button>
                </p>
              ))}
            </>}
          </div>

        </div>
        <button onClick={() => navigate('/globus')} className="backToGlobe">Powrót do globusa</button>
      </div>

      {/* Prawa strona - Galeria i Wpisy */}
      <div className='rightSide'>
        <div className='topRight'>
          {/* Poprawiony kontener nagłówka galerii */}
          <div className='galleryHeader'>
            <h1>Galeria</h1>
            <div className="uploadBox">
              <label className="uploadLabel">
                📷 Dodaj zdjęcia
                <input type="file" multiple accept="image/*" onChange={(e) => addPhoto(e)} />
              </label>
            </div>
          </div>

          {/* Galeria wyciągnięta POZA galleryHeader */}
          {pictures.length === 0 ? (
            <p style={{ fontSize: "1.2rem", marginTop: "20px" }}>
              Ten wpis jeszcze nie ma żadnych zdjęć
              <span style={{ fontSize: "0.9rem", display: "block", color: "#666" }}>(Uwaga: Nie można dodawać zdjęć bez wybrania wpisu)</span>
            </p>
          ) : (
            <div className='gallery'>
              {pictures.map((element) => (
                <div key={element.id} className='photoWrapper'>
                  <img src={`${API_URL}/pictures/${element.id}/image`} alt="Zdjęcie z wycieczki" className='item' />
                  <button className="actionDelete" onClick={() => deletePhoto(element.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='bottomRight'>
          <h1>Wpis</h1>
          {moreInfo ? (
            <>
              <p className='dateTop'>{moreInfo.arrivalDate}</p>
              <p className='tripDescription'>{moreInfo.description}</p>

              <div className='bottomDiv'>
                <p className='dateBottom'>{moreInfo.departureDate}</p>
                <div className='buttonsRow'>
                  <button className='deleteTrip' onClick={() => handleDeleteClick(moreInfo.id)}>Usuń wpis</button>
                  <button className='updateTrip' onClick={() => { localStorage.setItem("tripId", moreInfo.id); navigate('/edytuj-wpis'); }}>Edytuj wpis</button>
                  <button className='addTrip' onClick={() => navigate('/nowy-wpis', { state: { countryDbId: localStorage.getItem("selectedCountryDbId") } })}>Dodaj nowy wpis</button>
                </div>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: "1.2rem", marginTop: "20px", color: "#4a629b" }}>
                Dodaj wpis, aby wyświetlić wycieczkę z tego kraju
              </p>
              <div className='buttonsRow' style={{ position: "absolute", bottom: 0, right: 0, padding: "15px" }}>
                <button className='addTrip' onClick={() => navigate('/nowy-wpis', { state: { countryDbId: localStorage.getItem("selectedCountryDbId") } })}>Dodaj nowy wpis</button>
              </div>
            </>
          )}
        </div>
      </div>
      {showDeleteConfirmationWidow && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '24px 32px',
            borderRadius: '12px',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.25rem', color: '#1e293b' }}>
              Usuwanie wpisu
            </h3>
            <p style={{ color: '#64748b', margin: '0 0 24px 0', lineHeight: '1.5' }}>
              Czy na pewno chcesz usunąć ten wpis wraz ze wszystkimi zdjęciami?
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowDeleteConfirmationWidow(false)}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={confirmDeleteTrip}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#EF4444',
                  color: '#ffffff',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Okienko ładowania podczas wysyłania zdjęć */}
      {isUploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '30px 40px',
            borderRadius: '16px',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Animowany Spinner CSS */}
            <div className="upload-spinner"></div>

            <div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', color: '#1e293b' }}>
                Trwa wysyłanie zdjęć...
              </h3>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Proszę czekać, zdjęcia są zapisywane w bazie.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>


  );
};

export default Trip;